/* eslint-disable no-nested-ternary */
import classNames from "classnames";
import {
  CompanyParticipationType,
  ErrorMessages,
  ESubmittalStatus,
  EUserRoleName,
  EUserTypes,
  ProjectPermissionEnum,
  UserParticipationType
} from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { useContext, useMemo } from "react";
import { getLoggedinUserId } from "services/auth";
import { isProjectInIntegrationMode, matchUserProjectRole } from "utils/utils";

export const useSubmmittialDetails = (props: {
  featureInstanceData: any;
  isRevisionMode: boolean;
  submittal?: any;
  selectedRC?: any;
  isWorkflowInOverrideMode?: boolean;
  editableFields?: any;
  submittalViewState?: any;
}) => {
  const {
    featureInstanceData,
    isRevisionMode,
    submittal,
    selectedRC,
    isWorkflowInOverrideMode,
    editableFields,
    submittalViewState
  } = props;

  const { tokenContents, projectDetails } = useContext(ProjectContext);

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );
  const cannotCreateSubmittal = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateSubmittal,
    tokenContents?.role!
  );
  const cannotRespondToWorkflow = isPermissionNotGrantted(
    ProjectPermissionEnum.SubmittalWorkflow,
    tokenContents?.role!
  );
  const isSubmittalInDoneState =
    submittalViewState?.status === ESubmittalStatus.DONE;

  const isLoggedInUserGcAdmin =
    tokenContents?.role === EUserRoleName[EUserRoleName.gc_project_admin];

  const isLoggedInUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const isLoggedInUserSC = matchUserProjectRole(
    EUserTypes.SUBCONTRACTOR,
    tokenContents?.role
  );

  const isLoggedInUserArchitect = matchUserProjectRole(
    EUserTypes.ARCHITECT,
    tokenContents?.role
  );

  const isLoggedInUserEngineer = matchUserProjectRole(
    EUserTypes.ENGINEER,
    tokenContents?.role
  );

  const isLoggedInUserOwner = matchUserProjectRole(
    EUserTypes.OWNER,
    tokenContents?.role
  );

  const contentClassNames = classNames({
    "h-[330px]": !isLoggedInUserSC,
    "h-[250px]": isLoggedInUserSC
  });

  const submittalParticipants = useMemo(() => {
    const contractor = featureInstanceData?.feature_company_participants?.find(
      (company: any) =>
        company?.company_participation_type_id ===
        CompanyParticipationType.RESPONSIBLE_CONTRACTOR
    );

    const drCompany = featureInstanceData?.feature_company_participants?.find(
      (company: any) =>
        company?.company_participation_type_id ===
        CompanyParticipationType.DESIGN_REVIEWER_VENDOR
    );

    const assignee = featureInstanceData?.feature_user_paticipants?.find(
      (user: any) =>
        user?.user_participation_type?.id === UserParticipationType.ASSIGNEE
    );

    const assigneeCompany: any = assignee?.user?.project_users?.length
      ? assignee?.user?.project_users[0].subscription_vendor?.name ?? ""
      : "";

    const submitter = featureInstanceData?.feature_user_paticipants?.find(
      (user: any) =>
        user?.user_participation_type?.id === UserParticipationType.SUBMITTER
    );

    const gcReviewer = featureInstanceData?.feature_user_paticipants?.find(
      (user: any) =>
        user?.user_participation_type?.id === UserParticipationType.GC_REVIEWER
    );

    const drReviewer = featureInstanceData?.feature_user_paticipants?.find(
      (user: any) => {
        return (
          user?.user_participation_type?.id ===
          UserParticipationType.DESIGN_REVIEWER
        );
      }
    );

    const assigneeUnregistered = () => {
      if (assignee?.user_unregistered) {
        if (assignee?.user_unregistered === drReviewer?.user_unregistered) {
          return {
            id: `${drReviewer?.user_unregistered}`,
            fullName: `${drReviewer?.user_unregistered}`,
            company: {
              name:
                drCompany?.vendor_unregistered ?? drCompany?.vendor?.name ?? ""
            },
            type: "poc"
          };
        }
        return {
          id: `${submitter?.user_unregistered}`,
          fullName: `${submitter?.user_unregistered}`,
          company: {
            name:
              contractor?.vendor_unregistered ?? contractor?.vendor?.name ?? ""
          },
          type: "poc"
        };
      }
      return null;
    };

    return {
      contractor,
      assignee: assignee?.user
        ? assignee
        : assignee?.user_source_info
        ? {
            user: {
              id: `${assignee?.user_source_info?.first_name} ${assignee?.user_source_info?.last_name}`,
              first_name: `${assignee?.user_source_info?.first_name} ${assignee?.user_source_info?.last_name}`,
              vendor_source_info: assignee?.user_source_info?.vendor_source_info
            }
          }
        : null,
      submitter: submitter?.user
        ? submitter
        : submitter?.user_unregistered
        ? {
            user: {
              id: submitter.user_unregistered,
              first_name: submitter.user_unregistered
            }
          }
        : submitter?.user_source_info
        ? {
            user: {
              id: `${submitter?.user_source_info?.first_name} ${submitter?.user_source_info?.last_name}`,
              first_name: `${submitter?.user_source_info?.first_name} ${submitter?.user_source_info?.last_name}`,
              vendor_source_info:
                submitter?.user_source_info?.vendor_source_info
            }
          }
        : null,
      gcReviewer: gcReviewer?.user
        ? gcReviewer
        : gcReviewer?.user_source_info
        ? {
            user: {
              id: `${gcReviewer?.user_source_info?.first_name} ${gcReviewer?.user_source_info?.last_name}`,
              first_name: `${gcReviewer?.user_source_info?.first_name} ${gcReviewer?.user_source_info?.last_name}`,
              vendor_source_info:
                gcReviewer?.user_source_info?.vendor_source_info
            }
          }
        : null,
      drReviewer: drReviewer?.user
        ? drReviewer
        : drReviewer?.user_unregistered
        ? {
            user: {
              id: drReviewer.user_unregistered,
              first_name: drReviewer.user_unregistered
            }
          }
        : drReviewer?.user_source_info
        ? {
            user: {
              id: `${drReviewer?.user_source_info?.first_name} ${drReviewer?.user_source_info?.last_name}`,
              first_name: `${drReviewer?.user_source_info?.first_name} ${drReviewer?.user_source_info?.last_name}`,
              vendor_source_info:
                drReviewer?.user_source_info?.vendor_source_info
            }
          }
        : null,
      drCompany,
      assigneeCompany,
      assigneeUnregistered
    };
  }, [
    featureInstanceData?.feature_company_participants,
    featureInstanceData?.feature_user_paticipants
  ]);

  const loggedInUserId = useMemo(() => {
    return getLoggedinUserId();
  }, []);

  const isLoggedInUserWFGCUser =
    submittalParticipants?.gcReviewer?.user?.id === loggedInUserId;
  const isCurrentAssigneeGCUser =
    submittalParticipants?.assignee?.user?.id ===
    submittalParticipants?.gcReviewer?.user?.id;

  const isLoggedInUserMainInWorkflow =
    loggedInUserId === submittalParticipants?.gcReviewer?.user?.id ||
    loggedInUserId === submittalParticipants?.drReviewer?.user?.id ||
    loggedInUserId === submittalParticipants?.submitter?.user?.id;

  const showReviewOption =
    (isLoggedInUserMainInWorkflow || isLoggedInUserGC) &&
    !isLoggedInUserSC &&
    submittalViewState?.status === ESubmittalStatus.REVIEW;

  const checkOverrideForActualUsers =
    submittalParticipants?.assignee?.user?.id &&
    submittalParticipants?.assignee?.user?.id !== loggedInUserId;

  const checkOverrideForPOCUsers =
    submittal?.assignee_unregistered &&
    (submittal?.assignee_unregistered === submittal?.submitter_unregistered ||
      submittal?.assignee_unregistered ===
        submittal?.design_reviewer_unregistered);

  const showOverrideWorkflowBtn =
    isLoggedInUserGcAdmin &&
    (checkOverrideForActualUsers || checkOverrideForPOCUsers);

  const loggedInUser = featureInstanceData?.feature_user_paticipants?.find(
    (user: any) => user?.user?.id === loggedInUserId
  );

  const getGeneralInfoFieldsDisabledTooltipMsg = () => {
    if (isLoggedInUserSC) return "";
    if (isRevisionMode) return ErrorMessages.RevisionMode;
    if (tokenContents?.role === EUserRoleName[EUserRoleName.gc_project_admin])
      return "";
    if (isIntegrationMode)
      return ErrorMessages.FieldDisabledDueToIntegrationMode;
    if (cannotCreateSubmittal) return ErrorMessages.PermissionNotGranted;
    if (isSubmittalInDoneState)
      return "No actions can be performed as the submittal is already closed";
    if (submittalViewState?.status !== ESubmittalStatus.CREATE) {
      return "Details cannot be updated as workflow has started";
    }
    return "";
  };

  const generalInfoDisabledTooltipMsg =
    getGeneralInfoFieldsDisabledTooltipMsg();

  const getWorkflowFieldsDisabledTooltipMsg = () => {
    if (isLoggedInUserSC || !submittalViewState) return "";
    if (isIntegrationMode)
      return ErrorMessages.FieldDisabledDueToIntegrationMode;
    if (cannotCreateSubmittal) return ErrorMessages.PermissionNotGranted;
    if (isSubmittalInDoneState)
      return "No actions can be performed as the submittal is already closed";
    if (
      submittalViewState &&
      submittalViewState?.status !== ESubmittalStatus.CREATE
    ) {
      return "Details cannot be updated as workflow has started";
    }
    return "";
  };

  const getSubmitterDisabledTooltipMsg = () => {
    if (isLoggedInUserSC) return "";
    if (isIntegrationMode)
      return ErrorMessages.FieldDisabledDueToIntegrationMode;
    if (cannotCreateSubmittal) return ErrorMessages.PermissionNotGranted;
    if (isSubmittalInDoneState)
      return "No actions can be performed as the submittal is already closed";
    if (
      submittalViewState &&
      !cannotCreateSubmittal &&
      submittalViewState?.status !== ESubmittalStatus.CREATE
    ) {
      return "Details cannot be updated as workflow has started";
    }

    if (!cannotCreateSubmittal && !selectedRC) {
      return "First select a Responsible Contractor";
    }

    return "";
  };

  const getAttachBtnDisabledMsg = () => {
    if (isLoggedInUserSC) return "";
    if (cannotRespondToWorkflow) {
      return ErrorMessages.PermissionNotGranted;
    }

    if (isWorkflowInOverrideMode) return "";

    if (!editableFields?.includes("attachment")) {
      return "Only the Ball In court user can currently add attachments";
    }

    return "";
  };

  const getAssignDisabledTooltipMsg = () => {
    if (isWorkflowInOverrideMode || isRevisionMode) return "";

    if (cannotRespondToWorkflow) {
      return ErrorMessages.PermissionNotGranted;
    }

    if (submittalViewState?.status === ESubmittalStatus.DONE) {
      return "No actions can be performed as the submittal is already closed";
    }

    if (
      isLoggedInUserGC &&
      editableFields?.includes("comment") &&
      (!submittalParticipants?.gcReviewer?.user?.id ||
        !submittalParticipants?.drReviewer?.user?.id ||
        !submittalParticipants?.submitter?.user?.id)
    ) {
      let messageText = "To continue";
      const pendingUserSelect = [];
      if (!submittalParticipants?.submitter?.user?.id)
        pendingUserSelect.push("Submitter");
      if (!submittalParticipants?.gcReviewer?.user?.id)
        pendingUserSelect.push("GC Reviewer");
      if (!submittalParticipants?.drReviewer?.user?.id)
        pendingUserSelect.push("Design Reviewer");
      if (pendingUserSelect.length > 0)
        messageText += `, please select a ${pendingUserSelect.join(", ")}.`;

      return messageText;
    }

    if (
      !editableFields?.includes("comment") &&
      loggedInUserId === submittalParticipants?.assignee?.user?.id &&
      submittalParticipants?.assignee?.user?.id !==
        submittalParticipants?.gcReviewer?.user?.id &&
      submittalParticipants?.assignee?.user?.id !==
        submittalParticipants?.drReviewer?.user?.id &&
      submittalParticipants?.assignee?.user?.id !==
        submittalParticipants?.submitter?.user?.id
    ) {
      return "";
    }

    if (!editableFields?.includes("comment")) {
      return "Only The Ball In Court user can currently perform an action";
    }

    return "";
  };
  const assignDisabledTooltipMsg = getAssignDisabledTooltipMsg();

  return {
    isIntegrationMode,
    cannotRespondToWorkflow,
    cannotCreateSubmittal,
    isSubmittalInDoneState,
    generalInfoDisabledTooltipMsg,
    getWorkflowFieldsDisabledTooltipMsg,
    getSubmitterDisabledTooltipMsg,
    assignDisabledTooltipMsg,
    contentClassNames,
    submittalParticipants,
    showReviewOption,
    isLoggedInUserMainInWorkflow,
    getAttachBtnDisabledMsg,
    showOverrideWorkflowBtn,
    loggedInUserId,
    loggedInUser,
    permission: {
      isLoggedInUserGcAdmin,
      isLoggedInUserGC,
      isLoggedInUserSC,
      isLoggedInUserArchitect,
      isLoggedInUserEngineer,
      isLoggedInUserOwner,
      isLoggedInUserWFGCUser,
      isCurrentAssigneeGCUser
    }
  };
};
